// ⚠️ KIR-2659 deep-link contract
// This hook is the canonical encoder/decoder for the DataTable URL state.
// External-system deep links rely on the *exact* encoding shape it
// produces — specifically `<tableId>.<key>[<op>]=<csvValues>` (see
// statePersistence.ts).
//
// The desktop agent (`extensions/agent/pkg/notifications/coordinator.go`
// → `buildSummaryDeepLinkURL`) and IDE extension
// (`extensions/vscode-core/src/services/notifications/coordinator.ts`
// → `buildSummaryDeepLinkURL`) construct alert-summary banner URLs
// against this encoding without going through React, so they CANNOT
// observe a refactor here at compile time.
//
// If you change the URL key shape, the operator brackets, the CSV
// separator, or the tableId namespace, the Kirin agent/extension
// must be updated in lock-step or every "View Details" click from
// a KIR-2659 summary banner will land on an unfiltered alerts page.
// The contract is exercised by:
//   - extensions/agent/pkg/notifications/coordinator_test.go
//     (TestBuildSummaryDeepLinkURL_OrgContext et al.)
//   - extensions/vscode-core/src/services/notifications/__tests__/
//     coordinator.test.ts (describe buildSummaryDeepLinkURL)
// Run those tests against any encoding change here.
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  ColumnConfig,
  FilterValue,
  SortState,
  TableRowData,
} from '../types';
import {
  extractStatePersistenceConfig,
  extractSortPersistenceConfig,
  getPersistedFilters,
  getPersistedSort,
  getPersistedSearch,
  encodePersistedFiltersToParams,
  encodePersistedSortToParams,
  encodePersistedSearchToParams,
  encodePersistedExpandedToParams,
  applyEncodedParamsToSearchParams,
  clearTableParams,
  decodeFiltersFromSearchParams,
  decodeSortFromSearchParams,
  decodeSearchFromSearchParams,
  decodeExpandedFromSearchParams,
} from '../utils/statePersistence';

export interface UseDataTableStateEncodingOptions<TRow extends TableRowData> {
  tableId: string;
  filters: FilterValue[];
  sort?: SortState | null;
  search?: string;
  expandedRowIds?: Set<string>;
  columns: ColumnConfig<TRow>[];
  onHydrateFilters?: (filters: FilterValue[]) => void;
  onHydrateSort?: (sort: SortState) => void;
  onHydrateSearch?: (search: string) => void;
  onHydrateExpanded?: (expandedRowIds: Set<string>) => void;
  enabled?: boolean;
}

export interface UseDataTableStateEncodingResult {
  persistenceMap: Record<string, { urlKey: string }>;
  sortPersistenceMap: Record<string, { sortKey: string }>;
  persistedFilters: FilterValue[];
  persistedSort: SortState | null;
  persistedSearch: string;
  encodedParams: Record<string, string>;
  searchParams: URLSearchParams;
}

export const useDataTableStateEncoding = <TRow extends TableRowData>({
  tableId,
  filters,
  sort,
  search,
  expandedRowIds,
  columns,
  onHydrateFilters,
  onHydrateSort,
  onHydrateSearch,
  onHydrateExpanded,
  enabled = true,
}: UseDataTableStateEncodingOptions<TRow>): UseDataTableStateEncodingResult => {
  const hasHydratedRef = useRef(false);
  const skipNextSyncRef = useRef(false);
  const [routerSearchParams, setSearchParams] = useSearchParams();
  const initialSearchParamsRef = useRef(routerSearchParams);
  const persistenceMap = useMemo(
    () => (enabled ? extractStatePersistenceConfig(columns) : {}),
    [columns, enabled]
  );
  const sortPersistenceMap = useMemo(
    () => (enabled ? extractSortPersistenceConfig(columns) : {}),
    [columns, enabled]
  );

  const persistedFilters = useMemo(
    () => getPersistedFilters(filters, persistenceMap),
    [filters, persistenceMap]
  );
  const persistedSort = useMemo(
    () => getPersistedSort(sort ?? null, sortPersistenceMap),
    [sort, sortPersistenceMap]
  );
  const persistedSearch = useMemo(() => getPersistedSearch(search), [search]);

  const encodedParams = useMemo(() => {
    const filterParams = encodePersistedFiltersToParams(
      tableId,
      persistedFilters
    );
    const sortParams = encodePersistedSortToParams(
      tableId,
      persistedSort,
      sortPersistenceMap
    );
    const searchParams = encodePersistedSearchToParams(
      tableId,
      persistedSearch
    );
    const expandedParams = encodePersistedExpandedToParams(
      tableId,
      expandedRowIds ?? new Set()
    );
    const mergedParams = {
      ...filterParams,
      ...sortParams,
      ...searchParams,
      ...expandedParams,
    };
    return mergedParams;
  }, [
    persistedFilters,
    tableId,
    persistedSort,
    sortPersistenceMap,
    persistedSearch,
    expandedRowIds,
  ]);

  const searchParams = useMemo(
    () =>
      applyEncodedParamsToSearchParams(
        tableId,
        encodedParams,
        routerSearchParams
      ),
    [tableId, encodedParams, routerSearchParams]
  );

  useEffect(() => {
    if (hasHydratedRef.current) {
      return;
    }

    if (!enabled) {
      hasHydratedRef.current = true;
      return;
    }

    const params = initialSearchParamsRef.current;
    const decodedFilters = decodeFiltersFromSearchParams(
      tableId,
      persistenceMap,
      params
    );
    const decodedSort = decodeSortFromSearchParams(
      tableId,
      sortPersistenceMap,
      params
    );
    const decodedSearch = decodeSearchFromSearchParams(tableId, params);
    const decodedExpanded = decodeExpandedFromSearchParams(tableId, params);

    if (decodedFilters.length > 0 && onHydrateFilters) {
      skipNextSyncRef.current = true;
      onHydrateFilters(decodedFilters);
    }

    if (decodedSort && onHydrateSort) {
      skipNextSyncRef.current = true;
      onHydrateSort(decodedSort);
    }

    if (decodedSearch && onHydrateSearch) {
      skipNextSyncRef.current = true;
      onHydrateSearch(decodedSearch);
    }

    if (decodedExpanded.size > 0 && onHydrateExpanded) {
      skipNextSyncRef.current = true;
      onHydrateExpanded(decodedExpanded);
    }

    hasHydratedRef.current = true;
  }, [
    tableId,
    persistenceMap,
    sortPersistenceMap,
    onHydrateFilters,
    onHydrateSort,
    onHydrateSearch,
    onHydrateExpanded,
    enabled,
  ]);

  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    if (!enabled) {
      return;
    }

    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        clearTableParams(next, tableId);
        Object.entries(encodedParams).forEach(([key, value]) => {
          next.set(key, value);
        });
        return next;
      },
      { replace: true }
    );
  }, [encodedParams, tableId, enabled, setSearchParams]);

  return {
    persistenceMap,
    sortPersistenceMap,
    persistedFilters: enabled ? persistedFilters : [],
    persistedSort: enabled ? persistedSort : null,
    persistedSearch: enabled ? persistedSearch : '',
    encodedParams: enabled ? encodedParams : {},
    searchParams: enabled ? searchParams : new URLSearchParams(),
  };
};
